import { imgixHost } from 'src/config'
import Generic from 'src/models/generic/Generic'
import InventoryRecipe from 'src/models/inventory-recipe/InventoryRecipe'
import Item from 'src/models/item/Item'
import Model, { encoders, executeMutation, GqlError, ID, Integer, JsonArray, JsonObject, MODEL_LEVEL } from 'src/models/model'
import Archivable from 'src/models/model/concerns/Archivable'
import Librariable from 'src/models/model/concerns/Librariable'
import SalesChannelRestrictable, { MENU_DEFAULT as SALES_CHANNEL_MENU_DEFAULT } from 'src/models/model/concerns/SalesChannelRestrictable'
import ItemOrderItemModifier from 'src/models/order-item/ItemOrderItemModifier'
import OrderItem from 'src/models/order-item/OrderItem'
import PriceTierPrice from 'src/models/price-tier/PriceTierPrice'

const ACTIONS = {
  DISABLE_SALES: 'disable_sales',
  ENABLE_SALES: 'enable_sales'
}

export default class ItemVariation extends Model {
  static name = 'ItemVariation'

  static isConfiguration = true

  static deletable = true

  static versioned = true

  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Concession]

  static define () {
    this.concerns = [Archivable, Librariable, SalesChannelRestrictable]

    this.fields = {
      id: {
        type: ID,
        required: true
      },
      action: {
        type: String,
        input: true,
        default: null
      },
      item: {
        type: Item,
        reference: true,
        output: true
      },
      name: {
        type: String,
        required: true,
        translatable: true
      },
      image: {
        type: String,
        default: null
      },
      color: {
        type: String,
        default: null
      },
      description: {
        type: String,
        default: null
      },
      displayOrder: {
        type: Integer,
        default: 100
      },
      upcCode: {
        type: String,
        required: true
      },
      price: {
        type: Number,
        encode: encoders.cents,
        default: 0
      },
      overrideSalesChannels: {
        type: Boolean,
        default: false
      },
      cost: {
        type: Number,
        encode: encoders.cents,
        default: null
      },
      disableEditCostCents: {
        type: Boolean,
        output: true,
        excludeFromDefault: true
      },
      salesDisabled: {
        type: Boolean,
        default: false
      },
      hasRecipe: {
        type: Boolean,
        output: true,
        default: false
      },
      inventoryRecipes: {
        type: [InventoryRecipe],
        output: true,
        excludeFromDefault: true
      },
      priceTierPrices: {
        type: [PriceTierPrice],
        allowDestroy: true,
        acceptsNestedAttributes: true
      },
      inventoryAvailabilitiesAndConstraintsByLocationAndRegisterStation: {
        type: JsonObject,
        output: true,
        excludeFromDefault: true
      }
    }

    this.computed = {
      nameWithItem () {
        return `${this.item?.name} - ${this.name}`
      },
      requiresPreparation () {
        return this.item?.requiresPreparation || false
      },
      areSalesRequirementsUnmet () {
        return this.salesDisabled || this.item?.areSalesRequirementsUnmet || false
      },
      backgroundForImgix () {
        if (this.color) {
          return this.color.slice(1)
        } else {
          return 'FFFFFF'
        }
      },
      imageUrl () {
        if (!this.image) return ''

        return `${imgixHost}/${this.image}?w=100&h100&fit=crop&fill=solid&fill-color=${this.backgroundForImgix}&bg=${this.backgroundForImgix}`
      },
      inventoryStrategy () {
        return this.configuration.currentSite?.activeInventoryStrategy
      }
    }

    this.mutations = {
      addOrUpdateItemVariationPriceTiers: {
        arguments: {
          itemVariations: {
            type: JsonArray,
            required: true
          }
        },
        results: {
          success: Boolean,
          errors: [GqlError]
        }
      }
    }

    this.queries = {
      itemVariationByUpc: {
        arguments: {
          upcCode: {
            type: String,
            required: true
          }
        },
        results: {
          itemVariation: ItemVariation
        }
      },
      itemVariationAvailabilitiesAndConstraintsByLocationAndRegisterStation: {
        results: {
          value: Generic
        }
      }
    }
  }

  get salesChannelRestrictableChannelsDefault () {
    return SALES_CHANNEL_MENU_DEFAULT
  }

  get isSalesChannelInherited () {
    // If we don't override channels, we inherit channels from Item,
    // which can control availability regardless of this ItemVariation
    return !this.overrideSalesChannels
  }

  get searchString () {
    return `${this.name} ${this.item.searchString}`.toLowerCase()
  }

  customSortValue (itemVariation) {
    return itemVariation?.item?.name
  }

  saveItemVariationPriceTiers () {
    if (!this.isCollection) {
      return
    }

    const dirtyItemVariations = _(this.data).map(itemVariation => {
      if (itemVariation.isDirty) {
        return {
          id: itemVariation.id,
          priceTierPrices: _(itemVariation.priceTierPrices.data).map(priceTierPrice => {
            if (priceTierPrice.isRemote && (_.isNil(priceTierPrice.price) || priceTierPrice.price === '')) {
              return { id: priceTierPrice.id, _destroy: true }
            } else if (priceTierPrice.isDirty) {
              return priceTierPrice.deepEncode
            } else {
              return null
            }
          }).compact().value()
        }
      } else {
        return null
      }
    }).compact().value()

    const response = executeMutation('addOrUpdateItemVariationPriceTiers', {
      itemVariations: dirtyItemVariations
    },
    {
      success: Boolean,
      errors: {
        structure: {
          code: true,
          description: true
        }
      }
    })

    return response
  }

  invalidInventoryRecipeIds (selectedModifierIds = []) {
    if (_.isEmpty(selectedModifierIds)) return []

    /**
     * Recipe triggers that specify an unselected modifier are invalid.
     */
    return _(this.inventoryRecipes.data)
      .map(inventoryRecipe => {
        return _.filter(inventoryRecipe.inventoryRecipeTriggers.data, inventoryRecipeTrigger => {
          return inventoryRecipeTrigger.modifierId && !_.includes(selectedModifierIds, inventoryRecipeTrigger.modifierId)
            ? inventoryRecipe.id
            : null
        })
      })
      .flatten()
      .compact()
      .uniq()
      .value()
  }

  createOrderItemForSelf () {
    const defaultItemModifier = _(this.item.itemModifierSets.data).flatMap(itemModifierSet =>
      itemModifierSet.defaultModifiers.data.map(defaultModifier => {
        return new ItemOrderItemModifier({
          modifierId: defaultModifier.id,
          modifierSetId: itemModifierSet.modifierSetId
        })
      })
    ).value()

    return new OrderItem({
      type: 'item',
      itemOrderItem: {
        itemId: this.item.id,
        itemVariationId: this.id,
        itemModifiers: defaultItemModifier
      },
      voucher: null,
      voucherType: null,
      discount: null
    })
  }

  async disableSales () {
    this.action = ACTIONS.DISABLE_SALES

    try {
      const { errors } = await this.save()
      if (errors && errors.length) {
        this.$mh.showErrors(errors)
      }
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }

  async enableSales () {
    this.action = ACTIONS.ENABLE_SALES

    try {
      const { errors } = await this.save()
      if (errors && errors.length) {
        this.$mh.showErrors(errors)
      }
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }
}
